import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import DeleteHook from "../../../Hooks/DeleteHook";
import PostHooks from "../../../Hooks/PostHooks";
import UpdateHooks from "../../../Hooks/UpdateHooks";
import { useQuery } from "../../../Hooks/useApi";

const SuperAdminPatientReview = () => {
  const [refetch, setRefetch] = useState(false);
  const { data: reviewsResponse } = useQuery("/patientReview/getpatientReview");
  const patientReviews = reviewsResponse?.data || [];
  const [formData, setFormData] = useState({
    patientReviewrsName: "",
    videoUrl: "",
    videoTitle: "",
    videoDes: "",
  });

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (formData?._id) {
        await UpdateHooks(
          `https://servercareseebd.careseebd.com/api/v1/patientReview/updatePatientReview/${formData?._id}`,
          formData
        );
        toast.success("Patient review updated successfully!");
        setFormData({ patientReviewrsName: "", videoUrl: "", videoTitle: "", videoDes: "" });
        setRefetch(!refetch);
      } else {
        await PostHooks(
          `https://servercareseebd.careseebd.com/api/v1/patientReview/addPatientReview`,
          formData,
          "Patient review posted successfully!"
        );
        setFormData({
          patientReviewrsName: "",
          videoUrl: "",
          videoTitle: "",
          videoDes: "",
        });
        setTimeout(() => {
          setRefetch(!refetch);
        }, 1000);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="max-w-6xl mx-auto select-none space-y-6">
      {/* Header bar */}
      <div className="flex items-center gap-3 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
          <Icon icon="solar:videocamera-record-bold" className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-lg font-black text-slate-800 tracking-tight">Patient Video Testimonials</h1>
          <p className="text-xs text-slate-400 font-semibold mt-0.5">Manage patient video reviews, titles, and descriptions</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form panel */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4 h-fit">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider">
            {formData?._id ? "Edit Testimonial" : "Add Patient Video"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Patient Name</label>
              <input
                type="text"
                name="patientReviewrsName"
                onChange={handleChange}
                value={formData.patientReviewrsName}
                required
                placeholder="Jane Smith"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold"
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Video Title</label>
              <input
                type="text"
                name="videoTitle"
                onChange={handleChange}
                value={formData.videoTitle}
                required
                placeholder="My Caresee Recovery Story"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold"
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Embed / Video URL</label>
              <input
                type="text"
                name="videoUrl"
                onChange={handleChange}
                value={formData.videoUrl}
                required
                placeholder="https://youtube.com/embed/..."
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold"
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Video Description</label>
              <textarea
                name="videoDes"
                onChange={handleChange}
                value={formData.videoDes}
                required
                rows="4"
                placeholder="Brief description of the patient review (approx. 40 words)..."
                className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-medium"
              />
            </div>

            <div className="flex gap-2">
              {formData?._id && (
                <button
                  type="button"
                  onClick={() => {
                    setFormData({ patientReviewrsName: "", videoUrl: "", videoTitle: "", videoDes: "" });
                  }}
                  className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-500 font-extrabold text-[11px] transition-all hover:bg-slate-50 cursor-pointer"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className="flex-1 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-95 text-white font-extrabold text-[11px] rounded-xl shadow-md shadow-cyan-500/10 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
              >
                {formData?._id ? "Update Video" : "Add Video"}
              </button>
            </div>
          </form>
        </div>

        {/* List panel */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider">Active Patient Videos</h2>
          <div className="overflow-x-auto border border-slate-100 rounded-xl">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/75 border-b border-slate-100">
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Patient Name</th>
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Video Title</th>
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {patientReviews.length > 0 ? (
                  patientReviews.map((review) => (
                    <tr key={review?._id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 text-xs font-bold text-slate-800">
                        {review?.patientReviewrsName}
                      </td>
                      <td className="px-6 py-4 text-xs font-semibold text-indigo-655">
                        {review?.videoTitle}
                      </td>
                      <td className="px-6 py-4 text-xs font-medium text-slate-500 max-w-xs truncate" title={review?.videoDes}>
                        {review?.videoDes}
                      </td>
                      <td className="px-6 py-4 text-xs">
                        <div className="flex justify-end items-center gap-2">
                          <button
                            onClick={async () => {
                              setFormData(review);
                              scrollToTop();
                            }}
                            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-150 rounded-lg transition-all cursor-pointer"
                            title="Edit Review"
                          >
                            <Icon icon="basil:edit-outline" className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              DeleteHook({
                                setRefetch,
                                refetch,
                                url: `https://servercareseebd.careseebd.com/api/v1/patientReview/deletePatientReview/${review?._id}`,
                              })
                            }
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 border border-slate-200 hover:border-rose-150 rounded-lg transition-all cursor-pointer"
                            title="Delete Review"
                          >
                            <Icon icon="material-symbols:delete-outline" className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-10 text-xs text-slate-400">
                      No reviews added.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminPatientReview;
